import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { useClientPortalPaths } from "../../hooks/useClientPortalBase";
import {
  getGoogleAnalyticsSettings,
  updateGoogleAnalyticsSettings,
} from "../../api/clientIntegrationsApi";
import {
  inviteTeamMember,
  listTeamMembers,
  resendTeamInvitation,
  updateTeamMember,
  updateTeamMemberStatus,
} from "../../api/clientTeamApi";
import { useClientAuth } from "../../auth/ClientAuthProvider";
import {
  ClientPortalError,
  ClientPortalLoading,
} from "../../components/clientPortal/ClientPortalStates";
import { formatClientDate, formatClientDateTime } from "../../utils/clientPortalFormat";
import {
  CLIENT_ROLE_DESCRIPTIONS,
  formatClientRole,
  formatTeamMemberName,
  teamMemberStatusTone,
} from "../../utils/clientTeamFormat";

const EMPTY_INVITE = {
  email: "",
  firstName: "",
  lastName: "",
  role: "CLIENT_VIEWER",
  sendInvitation: true,
};

const ClientSettingsPage = () => {
  const { clientViewId } = useParams();
  const { permissions } = useOutletContext() || {};
  const { clientUser } = useClientAuth();
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState(EMPTY_INVITE);
  const [inviteSaving, setInviteSaving] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [actionUserId, setActionUserId] = useState("");
  const paths = useClientPortalPaths();
  const [gaSettings, setGaSettings] = useState({
    enabled: false,
    measurementId: "",
    internalAnalyticsEnabled: true,
  });
  const [gaPublicSlug, setGaPublicSlug] = useState("");
  const [gaHasPublishedSite, setGaHasPublishedSite] = useState(false);
  const [gaSaving, setGaSaving] = useState(false);
  const [gaError, setGaError] = useState("");

  const canManageTeam = Boolean(permissions?.manageSettings || permissions?.manageIam);
  const assignableRoles = useMemo(() => {
    if (teamData?.meta?.assignableRoles?.length) {
      return teamData.meta.assignableRoles;
    }
    if (clientUser?.role === "CLIENT_OWNER") {
      return ["CLIENT_OWNER", "CLIENT_MANAGER", "CLIENT_CONTRIBUTOR", "CLIENT_VIEWER"];
    }
    if (clientUser?.role === "CLIENT_MANAGER") {
      return ["CLIENT_MANAGER", "CLIENT_CONTRIBUTOR", "CLIENT_VIEWER"];
    }
    return [];
  }, [teamData?.meta?.assignableRoles, clientUser?.role]);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      if (canManageTeam) {
        const [teamBody, gaBody] = await Promise.all([
          listTeamMembers(clientViewId),
          getGoogleAnalyticsSettings(clientViewId),
        ]);
        setTeamData(teamBody);
        const settings = gaBody.settings || gaBody.draftSettings || {};
        setGaSettings({
          enabled: Boolean(settings.enabled),
          measurementId: settings.measurementId || "",
          internalAnalyticsEnabled: settings.internalAnalyticsEnabled !== false,
        });
        setGaPublicSlug(gaBody.publicSiteSlug || "");
        setGaHasPublishedSite(Boolean(gaBody.hasPublishedSite));
      }
    } catch (error) {
      setErrorMessage(error.message || "Unable to load settings.");
    } finally {
      setLoading(false);
    }
  }, [canManageTeam, clientViewId]);

  useEffect(() => {
    load();
  }, [load]);

  const users = teamData?.users || [];
  const activeCount = useMemo(
    () => users.filter((u) => u.status === "ACTIVE").length,
    [users]
  );
  const invitedCount = useMemo(
    () => users.filter((u) => u.status === "INVITED").length,
    [users]
  );

  const handleInvite = async (event) => {
    event.preventDefault();
    setInviteSaving(true);
    setInviteError("");
    try {
      const body = await inviteTeamMember(clientViewId, inviteForm);
      setInviteOpen(false);
      setInviteForm(EMPTY_INVITE);
      setSuccessMessage(
        body.devToken
          ? `Invitation sent. Dev invite link token: ${body.devToken}`
          : body.invitationSent
            ? "Invitation email sent. They can set a password to access the portal."
            : "Team member saved."
      );
      load();
    } catch (error) {
      setInviteError(error.message || "Unable to send invitation.");
    } finally {
      setInviteSaving(false);
    }
  };

  const handleRoleChange = async (user, role) => {
    setActionUserId(user.clientUserId);
    setErrorMessage("");
    try {
      await updateTeamMember(clientViewId, user.clientUserId, { role });
      setSuccessMessage(`Updated role for ${formatTeamMemberName(user)}.`);
      load();
    } catch (error) {
      setErrorMessage(error.message || "Unable to update role.");
    } finally {
      setActionUserId("");
    }
  };

  const handleDisable = async (user) => {
    if (!window.confirm(`Remove portal access for ${user.email}?`)) {
      return;
    }
    setActionUserId(user.clientUserId);
    setErrorMessage("");
    try {
      await updateTeamMemberStatus(clientViewId, user.clientUserId, {
        status: "DISABLED",
        reason: "Disabled from client portal settings",
      });
      setSuccessMessage(`${user.email} no longer has portal access.`);
      load();
    } catch (error) {
      setErrorMessage(error.message || "Unable to disable user.");
    } finally {
      setActionUserId("");
    }
  };

  const handleResend = async (user) => {
    setActionUserId(user.clientUserId);
    setErrorMessage("");
    try {
      const body = await resendTeamInvitation(clientViewId, user.clientUserId);
      setSuccessMessage(
        body.devToken
          ? `Invitation resent. Dev token: ${body.devToken}`
          : `Invitation resent to ${user.email}.`
      );
    } catch (error) {
      setErrorMessage(error.message || "Unable to resend invitation.");
    } finally {
      setActionUserId("");
    }
  };

  const isSelf = (userId) => userId === clientUser?.clientUserId;

  const handleSaveGoogleAnalytics = async (event) => {
    event.preventDefault();
    setGaSaving(true);
    setGaError("");
    setSuccessMessage("");
    try {
      const body = await updateGoogleAnalyticsSettings(clientViewId, {
        enabled: gaSettings.enabled,
        measurementId: gaSettings.measurementId.trim(),
        internalAnalyticsEnabled: gaSettings.internalAnalyticsEnabled,
      });
      const settings = body.settings || {};
      setGaSettings({
        enabled: Boolean(settings.enabled),
        measurementId: settings.measurementId || gaSettings.measurementId,
        internalAnalyticsEnabled: settings.internalAnalyticsEnabled !== false,
      });
      setSuccessMessage(body.message || "Google Analytics settings saved.");
    } catch (error) {
      setGaError(error.message || "Unable to save Google Analytics settings.");
    } finally {
      setGaSaving(false);
    }
  };

  if (loading) {
    return <ClientPortalLoading message="Loading settings…" />;
  }

  return (
    <>
      {canManageTeam ? (
        <section className="panel-card cp-settings-integrations">
          <h3>Google Analytics</h3>
          <p className="muted">
            Connect your public Golf Homes site to Google Analytics 4. Page views, searches, listing
            views, lead forms, and contact clicks are forwarded automatically when tracking is on.
          </p>
          {gaPublicSlug ? (
            <p className="muted cp-settings-ga-site">
              Public site:{" "}
              <a href={`/v/${gaPublicSlug}`} target="_blank" rel="noreferrer">
                /v/{gaPublicSlug}
              </a>
              {gaHasPublishedSite ? null : (
                <span className="cp-settings-ga-warning">
                  {" "}
                  — publish the site in Studio before visitors can be tracked.
                </span>
              )}
            </p>
          ) : null}
          <form className="cp-settings-ga-form" onSubmit={handleSaveGoogleAnalytics}>
            {gaError ? <p className="admin-data-health-error">{gaError}</p> : null}
            <label className="admin-users-field">
              <span>GA4 Measurement ID</span>
              <input
                type="text"
                placeholder="G-XXXXXXXXXX"
                value={gaSettings.measurementId}
                onChange={(e) =>
                  setGaSettings((current) => ({ ...current, measurementId: e.target.value }))
                }
                autoComplete="off"
                spellCheck={false}
              />
              <span className="muted cp-settings-role-hint">
                From Google Analytics → Admin → Data streams → your web stream.
              </span>
            </label>
            <label className="cp-settings-checkbox">
              <input
                type="checkbox"
                checked={gaSettings.enabled}
                onChange={(e) =>
                  setGaSettings((current) => ({ ...current, enabled: e.target.checked }))
                }
              />
              Enable Google Analytics on the public site
            </label>
            <label className="cp-settings-checkbox">
              <input
                type="checkbox"
                checked={gaSettings.internalAnalyticsEnabled}
                onChange={(e) =>
                  setGaSettings((current) => ({
                    ...current,
                    internalAnalyticsEnabled: e.target.checked,
                  }))
                }
              />
              Keep Midpoint internal analytics (dashboard metrics in this portal)
            </label>
            <button type="submit" className="btn btn-primary" disabled={gaSaving}>
              {gaSaving ? "Saving…" : "Save Google Analytics"}
            </button>
          </form>
        </section>
      ) : null}

      {successMessage ? (
        <section className="panel-card cp-settings-toast">
          <p className="muted">
            {successMessage}{" "}
            <button type="button" className="cv-button-link" onClick={() => setSuccessMessage("")}>
              Dismiss
            </button>
          </p>
        </section>
      ) : null}

      {errorMessage ? <ClientPortalError message={errorMessage} /> : null}

      {!canManageTeam ? (
        <section className="panel-card cp-settings-access-denied">
          <h3>Team administration</h3>
          <p className="muted">
            You do not have permission to manage team members for this client view. Contact an
            owner or manager if you need to add employees to the portal.
          </p>
          <p>
            <Link to={paths.view(clientViewId)} className="cp-vendors-back-link">
              ← Back to dashboard
            </Link>
          </p>
        </section>
      ) : (
        <section className="panel-card cp-settings-team">
          <div className="cp-settings-team-header">
            <div>
              <h3>Team &amp; portal access</h3>
              <p className="muted">
                Invite employees to the Golf Homes Client Portal. They receive access to all client
                views assigned to your organization.
              </p>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setInviteError("");
                setInviteOpen(true);
              }}
            >
              + Invite employee
            </button>
          </div>

          <div className="cp-settings-team-stats">
            <div className="cp-settings-stat">
              <span className="cp-settings-stat-value">{users.length}</span>
              <span className="cp-settings-stat-label">Total members</span>
            </div>
            <div className="cp-settings-stat">
              <span className="cp-settings-stat-value">{activeCount}</span>
              <span className="cp-settings-stat-label">Active</span>
            </div>
            <div className="cp-settings-stat">
              <span className="cp-settings-stat-value">{invitedCount}</span>
              <span className="cp-settings-stat-label">Pending invite</span>
            </div>
          </div>

          {users.length === 0 ? (
            <p className="muted cp-settings-empty">No team members yet. Invite your first employee.</p>
          ) : (
            <div className="cp-settings-team-table-wrap">
              <table className="cp-settings-team-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Last login</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const busy = actionUserId === user.clientUserId;
                    const self = isSelf(user.clientUserId);
                    const canEdit = !self && assignableRoles.length > 0;

                    return (
                      <tr key={user.clientUserId}>
                        <td>
                          <strong>{formatTeamMemberName(user)}</strong>
                          <div className="muted cp-settings-email">{user.email}</div>
                          {self ? (
                            <span className="cp-settings-you-badge">You</span>
                          ) : null}
                        </td>
                        <td>
                          {canEdit ? (
                            <select
                              className="cp-settings-role-select"
                              value={user.role}
                              disabled={busy}
                              onChange={(e) => handleRoleChange(user, e.target.value)}
                              aria-label={`Role for ${user.email}`}
                            >
                              {assignableRoles.map((role) => (
                                <option key={role} value={role}>
                                  {formatClientRole(role)}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span>{formatClientRole(user.role)}</span>
                          )}
                          <p className="cp-settings-role-hint muted">
                            {CLIENT_ROLE_DESCRIPTIONS[user.role]}
                          </p>
                        </td>
                        <td>
                          <span className={`cp-settings-status cp-settings-status--${teamMemberStatusTone(user.status)}`}>
                            {user.status === "INVITED" ? "Invited" : user.status === "ACTIVE" ? "Active" : user.status}
                          </span>
                          {user.invitedAt && user.status === "INVITED" ? (
                            <div className="muted cp-settings-subdate">
                              Invited {formatClientDate(user.invitedAt)}
                            </div>
                          ) : null}
                        </td>
                        <td>
                          {user.lastLoginAt ? formatClientDateTime(user.lastLoginAt) : "—"}
                        </td>
                        <td className="cp-settings-actions">
                          {!self && user.status === "INVITED" ? (
                            <button
                              type="button"
                              className="cv-button-link"
                              disabled={busy}
                              onClick={() => handleResend(user)}
                            >
                              Resend invite
                            </button>
                          ) : null}
                          {!self && user.status !== "DISABLED" ? (
                            <button
                              type="button"
                              className="cv-button-link cp-settings-danger-link"
                              disabled={busy}
                              onClick={() => handleDisable(user)}
                            >
                              Remove access
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="cp-settings-help">
            <h4>How access works</h4>
            <ul>
              <li>Invited employees receive an email to set their password and sign in.</li>
              <li>They can access every client view linked to your organization.</li>
              <li>Roles control what they can edit (content, leads, settings).</li>
            </ul>
          </div>
        </section>
      )}

      {inviteOpen ? (
        <div className="client-vendor-modal-backdrop" onClick={() => setInviteOpen(false)}>
          <div
            className="client-vendor-modal cp-settings-invite-modal"
            role="dialog"
            aria-label="Invite employee"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="client-vendor-modal-header">
              <h3>Invite employee</h3>
              <button type="button" className="cv-button-link" onClick={() => setInviteOpen(false)}>
                Close
              </button>
            </div>
            <form className="client-vendor-modal-form" onSubmit={handleInvite}>
              {inviteError ? <p className="admin-data-health-error">{inviteError}</p> : null}
              <label className="admin-users-field">
                <span>Work email</span>
                <input
                  type="email"
                  required
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="name@company.com"
                />
              </label>
              <div className="cp-settings-invite-split">
                <label className="admin-users-field">
                  <span>First name</span>
                  <input
                    type="text"
                    value={inviteForm.firstName}
                    onChange={(e) => setInviteForm((f) => ({ ...f, firstName: e.target.value }))}
                  />
                </label>
                <label className="admin-users-field">
                  <span>Last name</span>
                  <input
                    type="text"
                    value={inviteForm.lastName}
                    onChange={(e) => setInviteForm((f) => ({ ...f, lastName: e.target.value }))}
                  />
                </label>
              </div>
              <label className="admin-users-field">
                <span>Portal role</span>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm((f) => ({ ...f, role: e.target.value }))}
                >
                  {assignableRoles.map((role) => (
                    <option key={role} value={role}>
                      {formatClientRole(role)}
                    </option>
                  ))}
                </select>
                <span className="muted cp-settings-role-hint">
                  {CLIENT_ROLE_DESCRIPTIONS[inviteForm.role]}
                </span>
              </label>
              <label className="cp-settings-checkbox">
                <input
                  type="checkbox"
                  checked={inviteForm.sendInvitation}
                  onChange={(e) =>
                    setInviteForm((f) => ({ ...f, sendInvitation: e.target.checked }))
                  }
                />
                Send invitation email now
              </label>
              <button type="submit" className="btn btn-primary" disabled={inviteSaving}>
                {inviteSaving ? "Sending…" : "Send invitation"}
              </button>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default ClientSettingsPage;
